/**
 * CRUD dos seis catálogos do projeto.
 *
 * Uma família de hooks só, parametrizada pela rota, porque as seis entidades têm
 * exatamente a mesma forma na API: busca paginada, obter por id, criar,
 * atualizar, excluir e transferir.
 *
 * Toda escrita invalida o cache do catálogo daquele projeto — é o único lugar
 * onde a regra 1 (carrega uma vez por sessão) abre exceção, e abre porque foi a
 * própria pessoa que mudou o dado. Invalidar aqui é o que faz a linha nova
 * aparecer sem recarregar a tela.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"
import { chaves } from "./query"
import type { PaginaResponse } from "./tipos"

/** Tudo que a lista de cadastro precisa mostrar, qualquer que seja a entidade. */
export type ItemDeCadastro = {
  id: number
  projetoId: number
  descricao: string
  qtd: number
  [campo: string]: unknown
}

export type FiltroCadastro = {
  descricao?: string
  /** Só conectores: a lista é do componente escolhido. */
  componentes?: string
  ordem?: string
}

export function usarLista(rota: string, projetoId: number, filtro: FiltroCadastro, pagina: number) {
  return useQuery({
    queryKey: ["cadastro", rota, projetoId, filtro, pagina] as const,
    enabled: projetoId > 0,
    queryFn: () =>
      obter<PaginaResponse<ItemDeCadastro>>(rota, {
        projetoId,
        descricoes: filtro.descricao?.trim() || undefined,
        componentes: filtro.componentes || undefined,
        ordem: filtro.ordem,
        pagina,
      }),
  })
}

/**
 * Salvar: cria quando não há id, atualiza quando há.
 *
 * A trava do canário mora no cliente HTTP e vai barrar isto em qualquer projeto
 * que não seja o canário, em desenvolvimento. É de propósito — a API aponta para
 * produção — e o erro chega aqui como `ErroDeTrava`, que a tela mostra por
 * inteiro em vez de traduzir para "não foi possível salvar".
 */
export function usarSalvar(rota: string, projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados }: { id: number | null; dados: Record<string, unknown> }) => {
      if (id == null) {
        const r = await api.post<ItemDeCadastro>(rota, { ...dados, projetoId })
        return r.data
      }
      // O PUT não leva projetoId no corpo: declara para a trava.
      const r = await api.put<ItemDeCadastro>(`${rota}/${id}`, dados, noProjeto(projetoId))
      return r.data
    },
    onSuccess: () => invalidarCatalogos(qc, rota, projetoId),
  })
}

export function usarExcluir(rota: string, projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => { await api.delete(`${rota}/${id}`, noProjeto(projetoId)) },
    onSuccess: () => invalidarCatalogos(qc, rota, projetoId),
  })
}

/** Transferência (merge): reatribui os pinouts da origem para o destino e apaga a origem. */
export function usarTransferir(rota: string, projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ origemId, destinoId }: { origemId: number; destinoId: number }) => {
      await api.post(`${rota}/transferir`, { origemId, destinoId }, { params: { projetoId } })
    },
    onSuccess: () => invalidarCatalogos(qc, rota, projetoId),
  })
}

/**
 * Depois de escrever, o que precisa cair do cache: a lista da própria entidade,
 * o catálogo em memória que os filtros usam, e a grade de pinout — porque a
 * descrição que a grade mostra acabou de mudar.
 */
function invalidarCatalogos(qc: ReturnType<typeof useQueryClient>, rota: string, projetoId: number) {
  qc.invalidateQueries({ queryKey: ["cadastro", rota, projetoId] })
  qc.invalidateQueries({ queryKey: ["catalogo"] })
  qc.invalidateQueries({ queryKey: ["pinouts", projetoId] })
  // RN-05: a escrita marcou `projeto.atualizar`, e a faixa do projeto mostra isso.
  qc.invalidateQueries({ queryKey: chaves.projeto(projetoId) })
}
